import { DataBarsRow } from "@/components/data-bars/DataBars.tsx";
import { ExpandableDataBars } from "@/components/data-bars/ExpandableDataBars.tsx";
import { ReportingWidget } from "@/pages/reporting/components/ReportingWidget.tsx";
import { Box, ButtonIcon, FontAwesomeIcon } from "@skailer-com/uikit";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface DimensionBarsWidgetProps {
  title: string;
  caption?: string;
  tooltip?: ReactNode;
  rows: DataBarsRow[];
  sortable?: boolean;
}

export const DimensionBarsWidget = ({
  title,
  caption,
  tooltip,
  rows,
  sortable = true,
}: DimensionBarsWidgetProps) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const sortedRows = useMemo(
    () =>
      sortable
        ? [...rows].sort((left, right) => {
            const delta = (left.sortValue ?? left.percent) - (right.sortValue ?? right.percent);
            return sortDirection === "asc" ? delta : -delta;
          })
        : rows,
    [rows, sortDirection, sortable]
  );

  if (!rows.length) return null;

  const nextDirection = sortDirection === "desc" ? "asc" : "desc";
  return (
    <Box className="group/dimension-widget">
      <ReportingWidget
        title={title}
        caption={caption}
        tooltip={tooltip}
        actions={
          sortable && (
            <Box className="opacity-0 transition-opacity group-hover/dimension-widget:opacity-100 group-focus-within/dimension-widget:opacity-100">
              <ButtonIcon
                variant="plain"
                size="sm"
                aria-label={t(`common:sort_${nextDirection}`)}
                title={t(`common:sort_${nextDirection}`)}
                icon={
                  <FontAwesomeIcon
                    icon={`fa-light ${sortDirection === "desc" ? "fa-arrow-down-9-1" : "fa-arrow-down-1-9"}`}
                  />
                }
                onClick={() => setSortDirection(nextDirection)}
              />
            </Box>
          )
        }
        content={<ExpandableDataBars rows={sortedRows} />}
      />
    </Box>
  );
};
